import { AddonType } from 'models/addon.model';
import { MealType } from 'models/meal.model';
import { MemberListType } from 'models/member.model';
import { OrderMealType } from 'models/order.model';
import { FormattedOrderType } from 'pages/orders/basket/bulk';
import { geocodeByAddress, getLatLng } from 'react-places-autocomplete';
import { cleanObject } from 'utils/functions';

class BulkOrderService {
  AddMembers(
    members: MemberListType[],
    setOrderMeal: (arg: (prevState: OrderMealType) => OrderMealType) => void
  ) {
    const formattedMembers = members.map((member) => ({
      id: member.id,
      firstName: member.user.first_name,
      lastName: member.user.last_name
    }));

    setOrderMeal((prevState: OrderMealType) => ({
      ...prevState,
      members: formattedMembers
    }));
  }

  AddNote(note: string, setOrderMeal: (arg: (prevState: OrderMealType) => OrderMealType) => void) {
    setOrderMeal((prevState: OrderMealType) => ({
      ...prevState,
      order_note: note
    }));
  }

  AddAddon(a: AddonType, orderMeal: OrderMealType, setOrderMeal: (arg: OrderMealType) => void) {
    if (!orderMeal) return;
    let addons = [...orderMeal.addons];
    const addon = orderMeal.addons.find((_a) => _a.id === a.id);
    if (addon) {
      addons.filter((a) => a.id !== addon.id);
      const newAddon = orderMeal.addons.map((_a) => {
        if (_a.id === addon.id) {
          return { ..._a, quantity: _a.quantity + 1 };
        }
        return _a;
      });
      addons = newAddon;
    } else {
      addons.push({ ...a, quantity: 1 });
    }
    const newOrderMeal = { ...orderMeal, addons };
    setOrderMeal(newOrderMeal);
  }

  IncrementAddon(
    a: AddonType,
    orderMeal: OrderMealType,
    setOrderMeal: (arg: OrderMealType) => void
  ) {
    if (!orderMeal) return;
    const addon = orderMeal.addons.find((_a) => _a.id === a.id);
    if (!addon) return this.AddAddon(a, orderMeal, setOrderMeal);
    const addons = [
      ...orderMeal.addons.filter((a) => a.id !== addon.id),
      { ...addon, quantity: addon.quantity + 1 } as AddonType
    ];
    const newOrderMeal = { ...orderMeal, addons };
    setOrderMeal(newOrderMeal);
  }

  ReplaceAddon(
    a: AddonType,
    b: AddonType,
    orderMeal: OrderMealType,
    setOrderMeal: (arg: OrderMealType) => void
  ) {
    if (!orderMeal) return;
    const addons = [...orderMeal.addons].filter((_a) => _a.id !== a.id);
    addons.push({ ...b, quantity: 1 });
    const newOrderMeal = { ...orderMeal, addons };
    setOrderMeal(newOrderMeal);
  }

  DeleteAddon(a: AddonType, orderMeal: OrderMealType, setOrderMeal: (arg: OrderMealType) => void) {
    if (!orderMeal) return;
    const addons = orderMeal.addons.filter((_a) => _a.id !== a.id);
    const newOrderMeal = { ...orderMeal, addons };
    setOrderMeal(newOrderMeal);
  }

  AddMealToOrder(
    meal: MealType | null,
    bulkOrderMeals: OrderMealType[],
    orderMeal: OrderMealType,
    setBulkOrderMeals: (meals: OrderMealType[]) => void
  ) {
    if (meal) {
      const formattedMeals = cleanObject(orderMeal);
      setBulkOrderMeals([...bulkOrderMeals, formattedMeals as OrderMealType]);
    }
  }

  EditMealToOrder(
    meal: MealType | null,
    bulkOrderMeals: OrderMealType[],
    orderMeal: OrderMealType,
    setBulkOrderMeals: (meals: OrderMealType[]) => void
  ) {
    if (meal) {
      const newOrder = bulkOrderMeals.filter((_meal) => _meal.created_at !== orderMeal.created_at);
      setBulkOrderMeals([...newOrder, orderMeal]);
    }
  }

  FormatOrder(
    bulkOrderMeals: OrderMealType[],
    setFormattedOrders: (orders: FormattedOrderType[]) => void
  ) {
    const groupedOrderMeals: FormattedOrderType[] = bulkOrderMeals?.reduce(
      (acc: FormattedOrderType[], curr: OrderMealType) => {
        const found = acc.find(
          (itm: { brand: { id: string } }) => itm.brand.id === curr?.brand?.id
        );
        if (found) {
          found.meals?.push(curr);
        } else {
          acc.push({ brand: curr.brand, meals: [curr] });
        }

        return acc;
      },
      []
    );
    setFormattedOrders(groupedOrderMeals);
  }

  RemoveMealFromOrder(
    meal: OrderMealType,
    bulkOrderMeals: OrderMealType[],
    setBulkOrderMeals: (meals: OrderMealType[]) => void
  ) {
    const updatedFormattedOrders = bulkOrderMeals.filter((orderMeal) => orderMeal.id !== meal.id);
    setBulkOrderMeals(updatedFormattedOrders);
  }

  GetAssignedUsers(meal: OrderMealType) {
    if (meal.members && meal.members.length > 0) {
      switch (meal.members?.length) {
        case 1:
          return `${meal.members[0].firstName} ${meal.members[0].lastName}`;
        case 2:
          return `${meal.members[0].firstName} & ${meal.members[1].firstName}`;
        default:
          return `${meal.members[0].firstName} & Others`;
      }
    } else {
      return 'Assign users';
    }
  }

  UpdateMealUsers(
    meal: OrderMealType,
    members: MemberListType[],
    bulkOrderMeals: OrderMealType[],
    setBulkOrderMeals: (meals: OrderMealType[]) => void
  ) {
    const formattedMembers = members.map((member) => ({
      id: member.id,
      firstName: member.user.first_name,
      lastName: member.user.last_name
    }));

    const updatedOrderMeals = bulkOrderMeals.map((order) => {
      if (order.id === meal.id) {
        return {
          ...order,
          members: formattedMembers
        };
      }
      return order;
    });

    setBulkOrderMeals(updatedOrderMeals);
  }

  UpdateMealQuantity(
    type: string,
    meal: OrderMealType,
    bulkOrderMeals: OrderMealType[],
    setBulkOrderMeals: (meals: OrderMealType[]) => void
  ) {
    const updatedOrderMeals = bulkOrderMeals.map((order) => {
      if (order.id === meal.id) {
        return {
          ...order,
          quantity: type === 'add' ? order.quantity + 1 : order.quantity - 1
        };
      }
      return order;
    });
    setBulkOrderMeals(updatedOrderMeals);
  }
  FormatAddress = async (address: string, name: string) => {
    const results = await geocodeByAddress(address);
    const latLng = await getLatLng(results[0]);

    const formattedAddress = {
      name: name === '' ? 'Other' : name,
      address: results[0].formatted_address,
      street_number: results[0].address_components[0].long_name,
      lat: latLng.lat.toString(),
      lng: latLng.lng.toString()
    };
    return formattedAddress;
  };
}

export default new BulkOrderService();
